import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { company, services, saveContactSubmission } from "../mock";
import { Reveal, PageHero } from "../components/Shared";
import useSEO from "../hooks/useSEO";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const contactCards = [
  {
    icon: Phone,
    title: "Telefon",
    value: company.phone,
    href: company.phoneHref,
    color: "text-[#1e9cd7] bg-[#e8f5fc]",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp",
    value: company.whatsapp,
    href: company.whatsappHref,
    color: "text-[#22c55e] bg-[#dcfce7]",
  },
  {
    icon: Mail,
    title: "E-Posta",
    value: company.email,
    href: company.emailHref,
    color: "text-[#1e9cd7] bg-[#e8f5fc]",
  },
  {
    icon: MapPin,
    title: "Adres",
    value: `${company.address} — Haritada Göster`,
    href: company.mapsUrl,
    color: "text-[#1e9cd7] bg-[#e8f5fc]",
  },
];

const Iletisim = () => {
  useSEO({
    title: "İletişim - Teklif Alın",
    description:
      "Nikomedya Turizm iletişim: +90 262 321 46 61, info@nikomedyaturizm.com, İzmit/Kocaeli. Personel ve öğrenci taşımacılığı için hemen teklif alın.",
  });

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Lütfen ad, telefon ve mesaj alanlarını doldurun.");
      return;
    }
    setSending(true);
    // MOCK: Şimdilik tarayıcıda saklanıyor (backend eklendiğinde API'ye gönderilecek)
    setTimeout(() => {
      saveContactSubmission(form);
      toast.success("Mesajınız alındı! En kısa sürede size dönüş yapacağız.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
      setSending(false);
    }, 700);
  };

  return (
    <div>
      <PageHero
        title="İletişim"
        subtitle="Hizmetlerimiz hakkında bilgi almak ya da teklif talebinde bulunmak için bize ulaşın. Size en kısa sürede dönüş yapalım."
      />

      {/* Contact cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {contactCards.map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="feature-card block h-full bg-white border border-slate-100 rounded-2xl p-6"
                >
                  <div
                    className={`feature-icon w-13 h-13 p-3.5 rounded-xl inline-flex items-center justify-center ${c.color}`}
                  >
                    <c.icon size={24} />
                  </div>
                  <h3 className="mt-4 font-bold text-[#0d2b4f]">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-[#5b6b7d] break-words">
                    {c.value}
                  </p>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal direction="left">
              <div className="bg-[#f5f8fb] border border-slate-100 rounded-3xl p-8 sm:p-10 h-full">
                <h2 className="text-xl sm:text-2xl font-bold text-[#0d2b4f] tracking-tight">
                  Bize Yazın
                </h2>
                <p className="mt-2 text-sm text-[#5b6b7d]">
                  Formu doldurun, en kısa sürede size dönelim.
                </p>
                <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#0d2b4f]">
                        Ad Soyad *
                      </Label>
                      <Input
                        id="name"
                        placeholder="Adınız Soyadınız"
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        className="bg-white h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#0d2b4f]">
                        Telefon *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="05XX XXX XX XX"
                        value={form.phone}
                        onChange={(e) =>
                          setForm({ ...form, phone: e.target.value })
                        }
                        className="bg-white h-11"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#0d2b4f]">
                        E-Posta
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="ornek@mail.com"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        className="bg-white h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0d2b4f]">İlgilendiğiniz Hizmet</Label>
                      <Select
                        value={form.service}
                        onValueChange={(v) => setForm({ ...form, service: v })}
                      >
                        <SelectTrigger className="bg-white h-11">
                          <SelectValue placeholder="Hizmet seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (
                            <SelectItem key={s.id} value={s.title}>
                              {s.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="Diğer">Diğer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#0d2b4f]">
                      Mesajınız *
                    </Label>
                    <Textarea
                      id="message"
                      rows={5}
                      placeholder="Talebinizi kısaca açıklayın..."
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                      className="bg-white resize-none"
                    />
                  </div>
                  <div className="flex flex-wrap gap-4 pt-1">
                    <button
                      type="submit"
                      disabled={sending}
                      className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm disabled:opacity-60"
                    >
                      <Send size={16} />
                      {sending ? "Gönderiliyor..." : "Mesajı Gönder"}
                    </button>
                    <a
                      href={company.whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-whatsapp inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm"
                    >
                      <MessageCircle size={17} />
                      WhatsApp'tan Yaz
                    </a>
                  </div>
                </form>
              </div>
            </Reveal>

            <Reveal direction="right">
              <div className="h-full flex flex-col gap-5">
                <div className="rounded-3xl overflow-hidden border border-slate-100 shadow-lg flex-1 min-h-[380px]">
                  <iframe
                    title="Nikomedya Turizm Konum"
                    src={company.mapsEmbed}
                    className="w-full h-full min-h-[380px]"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="bg-[#0d2b4f] rounded-3xl p-7 flex items-start gap-4">
                  <span className="w-12 h-12 rounded-xl bg-[#1e9cd7]/20 text-[#1e9cd7] flex items-center justify-center shrink-0">
                    <Clock size={22} />
                  </span>
                  <div>
                    <h3 className="font-bold text-white">Çalışma Saatleri</h3>
                    <p className="mt-1 text-sm text-white/60 leading-relaxed">
                      Operasyonlarımız 7/24 devam eder. Ofisimiz hafta içi
                      08:00 - 18:00 saatleri arasında hizmetinizdedir.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Iletisim;
